import { useState } from 'react';
import {
  MapPin,
  Plus,
  MoreHorizontal,
  Edit2,
  Trash2,
  Loader2,
  Phone,
  Mail,
  AlertTriangle,
  Database,
  Copy,
  Check,
  Tag,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { LocationIcon } from '@/components/location/LocationIcon';
import { LocationIconSelector } from '@/components/location/LocationIconSelector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { JAMAICA_PARISHES } from '@/lib/jamaica';
import { useLocations, type Location, generateLocationCode } from '@/hooks/useLocations';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function Locations() {
  const { locations, isLoading, createLocation, updateLocation, deleteLocation } = useLocations();
  const { isSuperadmin, isAdmin, organization } = useAuth();
  // Show dev tools to superadmin/admin users OR in development mode (localhost)
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const canSeedData = isDev || isSuperadmin || isAdmin;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [codeUpdateDialogOpen, setCodeUpdateDialogOpen] = useState(false);
  const [pendingEditData, setPendingEditData] = useState<{
    location: Location;
    updates: Partial<Location>;
    newCode: string;
    oldCode: string | null;
  } | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);
  const [seedingLocationId, setSeedingLocationId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast('Copied!', {
      description: 'ID copied to clipboard',
    });
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address_line1: '',
    address_city: '',
    address_parish: '',
  });
  const [locationIcon, setLocationIcon] = useState<string | null>(null);
  const [locationIconUrl, setLocationIconUrl] = useState<string | null>(null);
  const [locationAccentColor, setLocationAccentColor] = useState<string | null>(null);
  const [defaultTrackInventory, setDefaultTrackInventory] = useState<boolean>(true);
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name) {
      setFormError('Location name is required');
      return;
    }

    try {
      await createLocation.mutateAsync({
        name: formData.name,
        phone: formData.phone || null,
        email: formData.email || null,
        address: {
          line1: formData.address_line1,
          city: formData.address_city,
          parish: formData.address_parish,
        },
      });
      setCreateDialogOpen(false);
      resetForm();
      toast.success('Location created', { description: `${formData.name} has been added` });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create location';
      setFormError(errorMessage);
      toast.error('Error creating location', { description: errorMessage });
    }
  };

  const handleEditLocation = async (e: React.FormEvent, updateCode: boolean = false) => {
    e.preventDefault();
    setFormError(null);

    if (!selectedLocation || !formData.name) {
      setFormError('Location name is required');
      return;
    }

    // Check if name has changed and we should prompt for code update
    const nameChanged = formData.name.trim() !== selectedLocation.name.trim();
    
    if (nameChanged && !updateCode && selectedLocation.code) {
      // Name changed and we have a code - ask user if they want to update the code
      const existingCodes = locations
        .filter(loc => loc.id !== selectedLocation.id)
        .map(loc => loc.code)
        .filter((code): code is string => !!code);
      
      const newCode = generateLocationCode(formData.name, existingCodes);
      
      // Build settings object with icon, appearance, and inventory defaults
      const currentSettings = (selectedLocation.settings as Record<string, unknown>) || {};
      const currentAppearance = (currentSettings.appearance as Record<string, unknown>) || {};
      const newSettings = {
        ...currentSettings,
        icon: locationIcon,
        icon_url: locationIconUrl,
        appearance: locationAccentColor ? { ...currentAppearance, accentColor: locationAccentColor } : currentAppearance,
        default_track_inventory: defaultTrackInventory,
      };
      
      setPendingEditData({
        location: selectedLocation,
        updates: {
          name: formData.name,
          phone: formData.phone || null,
          email: formData.email || null,
          address: {
            line1: formData.address_line1,
            city: formData.address_city,
            parish: formData.address_parish,
          },
          settings: newSettings,
        },
        newCode,
        oldCode: selectedLocation.code,
      });
      setCodeUpdateDialogOpen(true);
      return;
    }

    try {
      // Build settings object with icon, appearance, and inventory defaults
      const currentSettings = (selectedLocation.settings as Record<string, unknown>) || {};
      const currentAppearance = (currentSettings.appearance as Record<string, unknown>) || {};
      const newSettings = {
        ...currentSettings,
        icon: locationIcon,
        icon_url: locationIconUrl,
        appearance: locationAccentColor ? { ...currentAppearance, accentColor: locationAccentColor } : currentAppearance,
        default_track_inventory: defaultTrackInventory,
      };

      // Generate code for new locations without one
      let newCode = selectedLocation.code;
      if (!newCode && nameChanged) {
        const existingCodes = locations
          .filter(loc => loc.id !== selectedLocation.id)
          .map(loc => loc.code)
          .filter((code): code is string => !!code);
        newCode = generateLocationCode(formData.name, existingCodes);
      }

      await updateLocation.mutateAsync({
        id: selectedLocation.id,
        updates: {
          name: formData.name,
          code: newCode,
          phone: formData.phone || null,
          email: formData.email || null,
          address: {
            line1: formData.address_line1,
            city: formData.address_city,
            parish: formData.address_parish,
          },
          settings: newSettings,
        },
      });
      setEditDialogOpen(false);
      resetForm();
      toast.success('Location updated');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update location';
      setFormError(errorMessage);
      toast.error('Error updating location', { description: errorMessage });
    }
  };

  const handleCodeUpdateConfirm = async (shouldUpdateCode: boolean) => {
    if (!pendingEditData) return;

    try {
      const updates = {
        ...pendingEditData.updates,
        code: shouldUpdateCode ? pendingEditData.newCode : pendingEditData.oldCode,
      };

      await updateLocation.mutateAsync({
        id: pendingEditData.location.id,
        updates,
      });
      
      setCodeUpdateDialogOpen(false);
      setEditDialogOpen(false);
      setPendingEditData(null);
      resetForm();
      toast.success('Location updated', { 
        description: shouldUpdateCode 
          ? `Location code updated to ${pendingEditData.newCode}` 
          : 'Location code kept unchanged',
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update location';
      setFormError(errorMessage);
      toast.error('Error updating location', { description: errorMessage });
    }
  };

  const openDeleteDialog = (location: Location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleDeleteLocation = async () => {
    if (!locationToDelete) return;

    try {
      await deleteLocation.mutateAsync(locationToDelete.id);
      toast.success('Location deleted', { description: `${locationToDelete.name} has been removed` });
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete location';
      toast.error('Error deleting location', { description: errorMessage });
    }
  };

  const handleToggleActive = async (location: Location, isActive: boolean) => {
    try {
      await updateLocation.mutateAsync({
        id: location.id,
        updates: { is_active: isActive },
      });
      toast.success(isActive ? 'Location activated' : 'Location deactivated', {
        description: `${location.name} is now ${isActive ? 'active' : 'inactive'}`,
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update location status';
      toast.error('Error', { description: errorMessage });
    }
  };

  const openEditDialog = (location: Location) => {
    setSelectedLocation(location);
    const address = location.address as { line1?: string; city?: string; parish?: string; state?: string } | null;
    const settings = location.settings as { icon?: string; icon_url?: string; appearance?: { accentColor?: string }; default_track_inventory?: boolean } | null;
    setFormData({
      name: location.name,
      phone: location.phone || '',
      email: location.email || '',
      address_line1: address?.line1 || '',
      address_city: address?.city || '',
      address_parish: address?.parish || address?.state || '',
    });
    setLocationIcon(settings?.icon || null);
    setLocationIconUrl(settings?.icon_url || null);
    setLocationAccentColor(settings?.appearance?.accentColor || null);
    // Default to true if setting is undefined (new behavior)
    setDefaultTrackInventory(settings?.default_track_inventory !== false);
    setEditDialogOpen(true);
  };

  const handleSeedData = async (location: Location) => {
    if (!canSeedData) return;
    
    setSeedingLocationId(location.id);
    try {
      const { data, error } = await supabase.rpc('seed_test_data_for_location', {
        target_location_id: location.id,
      });
      
      if (error) throw error;
      
      const result = data as { success: boolean; today_orders?: number; last_week_orders?: number; error?: string };
      
      if (result.success) {
        toast.success('Test data seeded', {
          description: `Created ${result.today_orders} orders for today and ${result.last_week_orders} for last week at ${location.name}`,
        });
      } else {
        throw new Error(result.error || 'Failed to seed data');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to seed test data';
      toast.error('Error seeding data', {
        description: errorMessage,
      });
    } finally {
      setSeedingLocationId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      address_line1: '',
      address_city: '',
      address_parish: '',
    });
    setLocationIcon(null);
    setLocationIconUrl(null);
    setLocationAccentColor(null);
    setDefaultTrackInventory(true);
    setFormError(null);
    setSelectedLocation(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Location Management</h2>
          <p className="text-sm text-muted-foreground">
            Manage your organization's locations and locations
          </p>
        </div>
        <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Location
        </Button>
      </div>

      {/* Locations Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading locations...</span>
          </div>
        </div>
      ) : locations.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No locations yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add your first location to get started
              </p>
              <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Location
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {locations.map((location) => {
            const address = location.address as { line1?: string; city?: string; parish?: string; state?: string } | null;
            return (
              <Card key={location.id} className={cn(!location.is_active && 'opacity-60')}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <LocationIcon
                        icon={(location.settings as { icon?: string })?.icon}
                        imageUrl={(location.settings as { icon_url?: string })?.icon_url}
                        isActive={location.is_active}
                        size="md"
                      />
                      <div>
                        <h3 className="font-semibold">{location.name}</h3>
                        {(address?.city || address?.parish || address?.state) && (
                          <p className="text-sm text-muted-foreground">
                            {address.city}{address.city && (address.parish || address.state) ? ', ' : ''}{address.parish || address.state}
                          </p>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(location)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {canSeedData && (
                          <DropdownMenuItem 
                            onClick={() => handleSeedData(location)}
                            disabled={seedingLocationId === location.id}
                          >
                            {seedingLocationId === location.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Database className="h-4 w-4 mr-2" />
                            )}
                            Seed Test Data
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => openDeleteDialog(location)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2 mb-4">
                    {location.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="h-3.5 w-3.5" />
                        {location.phone}
                      </div>
                    )}
                    {location.email && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="h-3.5 w-3.5" />
                        {location.email}
                      </div>
                    )}
                    {address?.line1 && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        {address.line1}
                      </div>
                    )}
                  </div>

                  {/* Status Toggle */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-sm font-medium">Active</span>
                    <Switch
                      checked={location.is_active}
                      onCheckedChange={(checked) => handleToggleActive(location, checked)}
                    />
                  </div>

                  {/* Kiosk Configuration */}
                  <div className="mt-3 pt-3 border-t space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Kiosk Config</p>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">Org:</span>
                        <button
                          onClick={() => copyToClipboard(organization?.slug || location.organization_id, `org-${location.id}`)}
                          className="flex items-center gap-1 text-xs font-mono bg-muted px-2 py-0.5 rounded hover:bg-muted/80 transition-colors"
                        >
                          <span className="truncate max-w-[140px]">{organization?.slug || location.organization_id.slice(0, 8)}</span>
                          {copiedId === `org-${location.id}` ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">Location:</span>
                        <button
                          onClick={() => copyToClipboard(location.code || location.id, `loc-${location.id}`)}
                          className="flex items-center gap-1 text-xs font-mono bg-muted px-2 py-0.5 rounded hover:bg-muted/80 transition-colors"
                        >
                          <span className="truncate max-w-[140px]">{location.code || location.id.slice(0, 8)}</span>
                          {copiedId === `loc-${location.id}` ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Location Dialog */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <form onSubmit={handleCreateLocation}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Add New Location</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Create a new location for your organization
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody>
              <div className="grid gap-4">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">Location Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Downtown Store"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="store@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address_line1">Address</Label>
                  <Input
                    id="address_line1"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="address_city">City</Label>
                    <Input
                      id="address_city"
                      value={formData.address_city}
                      onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address_parish">Parish</Label>
                    <Select
                      value={formData.address_parish}
                      onValueChange={(value) => setFormData({ ...formData, address_parish: value })}
                    >
                      <SelectTrigger id="address_parish">
                        <SelectValue placeholder="Select parish" />
                      </SelectTrigger>
                      <SelectContent>
                        {JAMAICA_PARISHES.map((parish) => (
                          <SelectItem key={parish} value={parish}>
                            {parish}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={createLocation.isPending}>
                {createLocation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create Location
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Edit Location Dialog */}
      <ResponsiveModal open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <ResponsiveModalContent maxWidth="lg">
          <form onSubmit={handleEditLocation}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Edit Location</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Update location details
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody>
              <div className="grid gap-4">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="edit_name">Location Name *</Label>
                  <Input
                    id="edit_name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <LocationIconSelector
                  icon={locationIcon}
                  imageUrl={locationIconUrl}
                  accentColor={locationAccentColor}
                  onIconChange={setLocationIcon}
                  onImageChange={setLocationIconUrl}
                  onAccentColorChange={setLocationAccentColor}
                  organizationId={organization?.id}
                />

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_phone">Phone</Label>
                    <Input
                      id="edit_phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_email">Email</Label>
                    <Input
                      id="edit_email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_address_line1">Address</Label>
                  <Input
                    id="edit_address_line1"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_address_city">City</Label>
                    <Input
                      id="edit_address_city"
                      value={formData.address_city}
                      onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_address_parish">Parish</Label>
                    <Select
                      value={formData.address_parish}
                      onValueChange={(value) => setFormData({ ...formData, address_parish: value })}
                    >
                      <SelectTrigger id="edit_address_parish">
                        <SelectValue placeholder="Select parish" />
                      </SelectTrigger>
                      <SelectContent>
                        {JAMAICA_PARISHES.map((parish) => (
                          <SelectItem key={parish} value={parish}>
                            {parish}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Inventory Settings */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="default_track_inventory">Enable inventory tracking by default</Label>
                      <p className="text-sm text-muted-foreground">
                        New products will have inventory tracking enabled automatically
                      </p>
                    </div>
                    <Switch
                      id="default_track_inventory"
                      checked={defaultTrackInventory}
                      onCheckedChange={setDefaultTrackInventory}
                    />
                  </div>
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={updateLocation.isPending}>
                {updateLocation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Confirmation Dialog */}
      <ResponsiveModal 
        open={deleteDialogOpen} 
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setLocationToDelete(null);
        }}
      >
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <ResponsiveModalTitle>Delete Location</ResponsiveModalTitle>
                <ResponsiveModalDescription>
                  This action cannot be undone
                </ResponsiveModalDescription>
              </div>
            </div>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete <span className="font-semibold text-foreground">{locationToDelete?.name}</span>? 
              This will permanently remove the location and cannot be recovered.
            </p>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteDialogOpen(false);
                setLocationToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteLocation}
              disabled={deleteLocation.isPending}
            >
              {deleteLocation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete Location
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Code Update Confirmation Dialog */}
      <AlertDialog open={codeUpdateDialogOpen} onOpenChange={setCodeUpdateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Update Location ID?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You've changed the location name. Would you like to update the Location ID to match?
                </p>
                <div className="bg-muted rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Current ID:</span>
                    <code className="font-mono bg-background px-2 py-0.5 rounded">
                      {pendingEditData?.oldCode || 'None'}
                    </code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">New ID:</span>
                    <code className="font-mono bg-background px-2 py-0.5 rounded text-primary">
                      {pendingEditData?.newCode}
                    </code>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Note: The Location ID is used for kiosk URLs and device registration. 
                  Updating it may require reconfiguring any devices using the old ID.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setCodeUpdateDialogOpen(false);
              setPendingEditData(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <Button 
              variant="outline" 
              onClick={() => handleCodeUpdateConfirm(false)}
            >
              Keep Current ID
            </Button>
            <AlertDialogAction onClick={() => handleCodeUpdateConfirm(true)}>
              Update ID
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

